import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SummaryApi from '../common';
import { authHeader } from '../helpers/auth-header';
import useAuth from '../hooks/useAuth';
import { ToastCommon } from '../common/Toast';
import { useDispatch } from 'react-redux';
import { fetchCart } from '../store/cartSlice';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processedCashbackIds, setProcessedCashbackIds] = useState(new Set());

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    try {
      const { data } = await axios.get(SummaryApi.notificationUnreadCount.url, {
        headers: authHeader()
      });
      if (data?.success && data?.unreadCount !== undefined) {
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [isAuthenticated]);

  // Fetch notifications list
  const fetchNotifications = useCallback(async (params = {}) => {
    if (!isAuthenticated) {
      setNotifications([]);
      return { notifications: [], pagination: null };
    }

    setLoading(true);
    try {
      const { data } = await axios.get(SummaryApi.notificationList.url, {
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          type: params.type,
          isRead: params.isRead,
          unreadOnly: params.unreadOnly,
        },
        headers: authHeader()
      });

      if (data?.success && data?.data) {
        const notificationsList = data.data.notifications || [];
        
        // Check for new unread cashback notifications
        const newCashbackNotifications = notificationsList.filter(
          notif => notif.type === 'cashback' && !notif.isRead && !processedCashbackIds.has(notif.id)
        );
        
        // Show toast and refresh wallet for new cashback notifications
        if (newCashbackNotifications.length > 0 && isAuthenticated) {
          newCashbackNotifications.forEach(notif => {
            // Extract cashback amount from message
            const amountMatch = notif.message.match(/₹([\d,]+\.?\d*)/);
            const amount = amountMatch ? amountMatch[1] : '';
            
            // Show toast notification
            ToastCommon.success(`💰 Cashback Received! You received ₹${amount} cashback!`);
            
            // Mark as processed
            setProcessedCashbackIds(prev => new Set([...prev, notif.id]));
          });
          
          // Refresh wallet balance when cashback notification is received
          dispatch(fetchCart());
        }
        
        // Check for new unread referral notifications
        const newReferralNotifications = notificationsList.filter(
          notif => notif.type === 'referral' && !notif.isRead
        );
        
        // Show toast for new referral notifications
        if (newReferralNotifications.length > 0 && isAuthenticated) {
          newReferralNotifications.forEach(notif => {
            // Extract level and amount from message
            const levelMatch = notif.message.match(/Level (\d+)/i);
            const amountMatch = notif.message.match(/₹([\d,]+\.?\d*)/);
            const level = levelMatch ? levelMatch[1] : '';
            const amount = amountMatch ? amountMatch[1] : '';
            
            // Show toast notification with level info
            if (level && amount) {
              ToastCommon.success(`🎁 Referral Reward! You earned ₹${amount} at Level ${level}!`);
            } else if (amount) {
              ToastCommon.success(`🎁 Referral Reward! You earned ₹${amount}!`);
            } else {
              ToastCommon.success(`🎁 New Referral Notification!`);
            }
          });
        }
        
        setNotifications(notificationsList);
        
        // Update unread count if provided
        if (data.data.unreadCount !== undefined) {
          setUnreadCount(data.data.unreadCount);
        }
        
        return {
          notifications: notificationsList,
          pagination: data.data.pagination,
          unreadCount: data.data.unreadCount
        };
      }
      return { notifications: [], pagination: null };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { notifications: [], pagination: null };
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!isAuthenticated) {
      ToastCommon.warning('Please login to mark notifications as read');
      return false;
    }

    try {
      const { data } = await axios.put(
        `${SummaryApi.notificationMarkRead.url}/${notificationId}/read`,
        {},
        { headers: authHeader() }
      );

      if (data?.success) {
        // Update local state
        setNotifications(prev => {
          const updated = prev.map(notif =>
            notif.id === notificationId ? { ...notif, isRead: true } : notif
          );
          
          // If marking cashback notification as read, refresh wallet
          const markedNotif = prev.find(n => n.id === notificationId);
          if (markedNotif?.type === 'cashback' && isAuthenticated) {
            dispatch(fetchCart());
          }
          
          return updated;
        });
        // Refresh unread count
        fetchUnreadCount();
        return true;
      } else {
        ToastCommon.error(data?.message || 'Failed to mark notification as read');
        return false;
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      const errorMessage = error.response?.data?.message || 'Failed to mark notification as read';
      ToastCommon.error(errorMessage);
      return false;
    }
  }, [isAuthenticated, fetchUnreadCount]);

  // Mark all notifications as read - Single API call for performance
  const markAllAsRead = useCallback(async (showToast = true) => {
    if (!isAuthenticated) {
      if (showToast) {
        ToastCommon.warning('Please login to mark notifications as read');
      }
      return false;
    }

    try {
      // Single bulk API call - no loop needed
      const { data } = await axios.put(
        SummaryApi.notificationMarkAllRead.url,
        {},
        { headers: authHeader() }
      );

      if (data?.success) {
        // Update local state - mark all as read
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
        if (showToast) {
          ToastCommon.success('All notifications marked as read');
        }
        return true;
      } else {
        if (showToast) {
          ToastCommon.error(data?.message || 'Failed to mark all notifications as read');
        }
        return false;
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      const errorMessage = error.response?.data?.message || 'Failed to mark all notifications as read';
      if (showToast) {
        ToastCommon.error(errorMessage);
      }
      return false;
    }
  }, [isAuthenticated]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId, showToast = true) => {
    if (!isAuthenticated) {
      if (showToast) {
        ToastCommon.warning('Please login to delete notifications');
      }
      return false;
    }

    try {
      const { data } = await axios.delete(
        `${SummaryApi.notificationDelete.url}/${notificationId}`,
        { headers: authHeader() }
      );

      if (data?.success) {
        // Remove from local state
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
        // Refresh unread count
        fetchUnreadCount();
        if (showToast) {
          ToastCommon.success('Notification deleted');
        }
        return true;
      } else {
        if (showToast) {
          ToastCommon.error(data?.message || 'Failed to delete notification');
        }
        return false;
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete notification';
      if (showToast) {
        ToastCommon.error(errorMessage);
      }
      return false;
    }
  }, [isAuthenticated, fetchUnreadCount]);

  // Delete all read notifications - Single API call for performance
  const deleteAllRead = useCallback(async () => {
    if (!isAuthenticated) {
      ToastCommon.warning('Please login to delete notifications');
      return false;
    }

    try {
      // Single bulk API call - no loop needed
      const { data } = await axios.delete(
        SummaryApi.notificationDeleteRead.url,
        { headers: authHeader() }
      );

      if (data?.success) {
        // Remove read notifications from local state
        setNotifications(prev => prev.filter(notif => !notif.isRead));
        // Refresh unread count
        fetchUnreadCount();
        ToastCommon.success('All read notifications deleted');
        return true;
      } else {
        ToastCommon.error(data?.message || 'Failed to delete read notifications');
        return false;
      }
    } catch (error) {
      console.error('Error deleting all read notifications:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete read notifications';
      ToastCommon.error(errorMessage);
      return false;
    }
  }, [isAuthenticated, fetchUnreadCount]);

  // Fetch unread count on mount and when auth changes
  useEffect(() => {
    fetchUnreadCount();
    
    // Poll for unread count every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

